
/**
 * Compresses an image file or blob to a maximum size
 * @param {File|Blob} file - The image file or blob to compress
 * @param {number} maxSizeMB - Maximum file size in MB (default: 1.5)
 * @param {number} maxWidthOrHeight - Maximum width or height in pixels (default: 1920)
 * @returns {Promise<Blob>} - Compressed image blob
 */
export const compressImage = async (file, maxSizeMB = 1.5, maxWidthOrHeight = 1920) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    
    reader.onload = (e) => {
      const img = new Image()
      
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let width = img.width
        let height = img.height
        
        // Calculate new dimensions while maintaining aspect ratio
        if (width > height) {
          if (width > maxWidthOrHeight) {
            height = (height * maxWidthOrHeight) / width
            width = maxWidthOrHeight
          }
        } else {
          if (height > maxWidthOrHeight) {
            width = (width * maxWidthOrHeight) / height
            height = maxWidthOrHeight
          }
        }
        
        canvas.width = width
        canvas.height = height
        
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        
        // Start with quality 0.9 and reduce if needed
        let quality = 0.9
        const maxSizeBytes = maxSizeMB * 1024 * 1024
        
        const tryCompress = () => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'))
                return
              }
              
              // If size is acceptable or quality is too low, return the blob
              if (blob.size <= maxSizeBytes || quality <= 0.1) {
                resolve(blob)
              } else {
                // Reduce quality and try again
                quality -= 0.1
                tryCompress()
              }
            },
            'image/jpeg',
            quality
          )
        }
        
        tryCompress()
      }
      
      img.onerror = () => {
        reject(new Error('Failed to load image'))
      }
      
      img.src = e.target.result
    }
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }
    
    reader.readAsDataURL(file)
  })
}
